import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Skeleton,
  Divider,
  Stack,
  Button,
  Icon,
  Tooltip,
  AppBar,
  Dialog,
  IconButton,
  Toolbar,
  Link as MuiLink,
} from '@mui/material'
import { IntegrationGroupCard, IntegrationGroupImage } from '@sendhub/integrations-ui'
import {
  GroupByEnum,
  Integration,
  IntegrationConfig,
  IntegrationConfigCreationPayload,
  IntegrationConfigSettingsFollowUpBossActionPlanSMS,
  IntegrationConfigSettingsFollowUpBossSyncContacts,
  IntegrationConfigSettingsJazzHRSyncApplicants,
  SyncDirectionEnum,
} from '@sendhub/integrations/api-client-typescript-axios'
import { forwardRef, Ref, SyntheticEvent, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Route, Link, Routes, useParams, Outlet, useNavigate } from 'react-router-dom'
import { selectAllIntegrationGroups, selectIntegrationGroupById } from '../../../store/integration-group/integration-groups.slice'
import { selectAllIntegrations, selectIntegrationById, getIntegrationsState } from '../../../store/integration/integrations.slice'
import { ChevronLeft } from '@mui/icons-material'
import SelectAccount from '../../select-account'
import {
  createIntegrationConfig,
  selectAllIntegrationConfigs,
  selectIntegrationConfigsLoadingStatus,
} from '../../../store/integration-config/integration-configs.slice'
import { useAppDispatch } from '../../../store/root'
import { ConfigureFubSyncContacts } from '../../integration-configuration-form/fub/sync-contacts'
import { ConfigureFubActionPlanSms } from '../../integration-configuration-form/fub/action-plan-sms'
import { ConfigureJazzhrSyncApplicants } from '../../integration-configuration-form/jazzhr/sync-applicants'
import { ConfigureSendhubWebhookRepeater } from '../../integration-configuration-form/sendhub/webhook-repeater'
import { ConfigureFubSyncSms } from '../../integration-configuration-form/fub/sync-sms'
import { selectAllZapierTemplates, selectZapierTemplatesByAppId } from '../../../store/zapier/zapier-templates.slice'
import { selectZapierAppById } from '../../../store/zapier/zapier-apps.slice'
import { selectFirstZapierServiceAccount, selectServiceAccountsLoadingStatus } from '../../../store/service-account/service-accounts.slice'
import Image from 'mui-image'
import CloseIcon from '@mui/icons-material/Close'
import { PropsOf } from '@emotion/react'
import { TransitionProps } from '@mui/material/transitions'
import Slide from '@mui/material/Slide'
import { ZapierTabPanel } from '../../zapier-tab-panel'
import { INTEGRATIONS_BASE_URL } from '../../../../utils/config'
import { selectIsSubUser, useIntegrationsAccess } from '../../../store/integrations-access'

const defaultIntegrationConfigSettings: Record<string, IntegrationConfigCreationPayload['settings']> = {
  'follow_up_boss/action_plan_sms': {
    numberFilter: 'All phone numbers a contact has',
    sendWindowStart: '09:00',
    sendWindowEnd: '17:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
  'follow_up_boss/sync_contacts': { groupBy: GroupByEnum.Tags, syncDirection: SyncDirectionEnum.FUBToSendHub },
  'follow_up_boss/sync_sms': { syncDirection: SyncDirectionEnum.SendHubToFUB },
  'jazzhr/sync_applicants_to_sendhub': { conditionGroups: [], lastNameFirst: true },
  'sendhub/webhook_repeater': { urls: [] },
}

// Optional plain-language blurb shown under an integration's name/description.
// Integrations without an entry simply show nothing extra.
const integrationAboutText: Record<string, string> = {
  'follow_up_boss/action_plan_sms':
    'When an action plan creates a Task with the Type set to "Text", this integration automatically sends a text from SendHub within the send window you set below.',
}

export function ConfigurationComponent({ integrationConfig }: { integrationConfig: IntegrationConfig }) {
  switch (integrationConfig.integrationId) {
    case 'follow_up_boss/action_plan_sms':
      return <ConfigureFubActionPlanSms integrationConfig={integrationConfig} />
    case 'follow_up_boss/sync_contacts':
      return <ConfigureFubSyncContacts integrationConfig={integrationConfig} />
    case 'follow_up_boss/sync_sms':
      return <ConfigureFubSyncSms integrationConfig={integrationConfig} />
    case 'jazzhr/sync_applicants_to_sendhub':
      return <ConfigureJazzhrSyncApplicants integrationConfig={integrationConfig} />
    case 'sendhub/webhook_repeater':
      return <ConfigureSendhubWebhookRepeater integrationConfig={integrationConfig} />

    default:
      return null
  }
}

export function TabPanel() {
  const { integrationGroupId, integrationSlug } = useParams<IntegrationGroupDetailsPageParams>()

  if (integrationSlug === 'zapier') return <ZapierTabPanel />
  return <DirectIntegrationTabPanel />
}

export function DirectIntegrationTabPanel() {
  const { integrationGroupId, integrationSlug } = useParams<IntegrationGroupDetailsPageParams>()

  const zapierApp = useSelector(selectZapierAppById(`${integrationGroupId}`.replaceAll(/_/g, '-')))
  const zapierAppTemplates = useSelector(selectZapierTemplatesByAppId(`${integrationGroupId}`.replaceAll(/_/g, '-')))
  const zapierServiceAccount = useSelector(selectFirstZapierServiceAccount)
  const serviceAccountsLoadingStatus = useSelector(selectServiceAccountsLoadingStatus)
  const isLoggedInToZapier = false
  const [zapierModelUrl, setZapierModelUrl] = useState('')

  const integrationId = `${integrationGroupId}/${integrationSlug}`
  const integration = useSelector(selectIntegrationById(integrationId))
  const integrationConfigsLoadingStatus = useSelector(selectIntegrationConfigsLoadingStatus)
  const integrationConfigs = useSelector(selectAllIntegrationConfigs).filter((iC) => iC.integrationId === integrationId)
  const dispatch = useAppDispatch()
  const { canEdit } = useIntegrationsAccess()
  const isSubUser = useSelector(selectIsSubUser)

  useEffect(() => {
    console.log('integration:', integration)
  }, [integration])

  // Create IntegrationConfig if none exist (main accounts with edit only).
  // Sub-users inherit the owner's configs; auto-create would write an empty
  // Action Plan SMS form under the sub-user id.
  useEffect(() => {
    if (
      canEdit &&
      !isSubUser &&
      !integrationConfigs.length &&
      integrationSlug &&
      integrationConfigsLoadingStatus === 'loaded' &&
      integrationSlug !== 'zapier'
    ) {
      dispatch(createIntegrationConfig({ enabled: false, integrationId, settings: defaultIntegrationConfigSettings[integrationId] }))
    }
  }, [canEdit, isSubUser, integrationConfigs, dispatch, integrationId, integrationSlug, integrationConfigsLoadingStatus])

  return (
    <Box sx={{ flexGrow: 1 }} role="tabpanel" id={`vertical-tabpanel-${integrationId}`} aria-labelledby={`vertical-tab-${integrationId}`}>
      <>
        {!integration || !integrationConfigs.length ? (
          integration && integrationConfigsLoadingStatus === 'loaded' && !canEdit ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body1">This integration is not configured yet.</Typography>
            </Box>
          ) : null
        ) : (
          <>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">{integration.name}</Typography>
              <Typography variant="subtitle1">{integration.description}</Typography>
              {integrationAboutText[integrationId] && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {integrationAboutText[integrationId]}
                </Typography>
              )}
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography sx={{ paddingBottom: 2 }} variant="body1">
                Configure Integration
              </Typography>
              <ConfigurationComponent integrationConfig={integrationConfigs[0]} />
            </Box>
          </>
        )}
      </>
    </Box>
  )
}

function a11yProps(integrationId: string) {
  return {
    id: `vertical-tab-${integrationId}`,
    'aria-controls': `vertical-tabpanel-${integrationId}`,
  }
}

export interface IntegrationGroupDetailsPageParams extends Record<string, string | undefined> {
  integrationGroupId: string
  integrationSlug: string
}

export function IntegrationGroupDetailsPage() {
  const params = useParams<IntegrationGroupDetailsPageParams>()
  const { integrationGroupId, integrationSlug } = params
  const integrationGroup = useSelector(selectIntegrationGroupById(`${integrationGroupId}`))
  const zapierAppId = `${integrationGroupId}`.replaceAll(/_/g, '-')
  const zapierApp = useSelector(selectZapierAppById(zapierAppId))
  const zapierAppTemplates = useSelector(selectZapierTemplatesByAppId(zapierAppId))

  const integrations = useSelector(selectAllIntegrations).filter((i) => i.integrationGroupId === integrationGroupId)
  const zapTemplates = useSelector(selectAllZapierTemplates)
  const navigate = useNavigate()

  useEffect(() => {
    if (!integrationSlug && integrations.length) {
      navigate('/integrations/' + integrations[0].id, { replace: true })
    }
  }, [params, integrations, navigate])
  useEffect(() => {
    console.log('zapierApp:', zapierApp)
  }, [zapierApp])

  // if (!integrations.length || !integrationSlug) return <Skeleton />

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ maxWidth: 1000 }}>
        <AllIntegrationsBackButton />
        {/* This paper is the whole details page component */}
        <Paper sx={{ flexGrow: 1, flexWrap: 'wrap', bgcolor: 'background.paper', display: 'flex', margin: 2 }}>
          {/* Header */}
          <Box sx={{ width: '100%', display: 'inlineBlock' }}>
            <IntegrationGroupCard
              sx={{ margin: 0 }}
              elevation={0}
              imageUrl={integrationGroup?.image ? `${INTEGRATIONS_BASE_URL}${integrationGroup?.image}` : zapierApp?.image || ''}
              title={integrationGroup?.name || zapierApp?.title || ''}
            />

            <Divider />
          </Box>

          <Stack direction="row" width="100%">
            {integrations.length === 0 ? null : (
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={integrationSlug ? `${integrationGroupId}/${integrationSlug}` : false}
                aria-label="Integrations"
                sx={{ borderRight: 1, borderColor: 'divider', maxWidth: 169, minWidth: '7rem' }}
              >
                {/* <Typography sx={{ p: 2, justifySelf: 'center' }}>Direct Integrations</Typography> */}
                {integrations.map((i) => (
                  <Tab
                    label={i.name}
                    key={i.id}
                    value={i.id}
                    to={`/integrations/${i.id}`}
                    component={Link}
                    replace
                    // sx={{ whiteSpace: 'nowrap' }}
                    {...a11yProps(i.id)}
                  />
                ))}
                <Tab
                  label={'zapier'}
                  key={'zapier'}
                  value={`${integrationGroupId}/zapier`}
                  to={`/integrations/${integrationGroupId}/zapier`}
                  component={Link}
                  replace
                  sx={{ borderTop: 1, borderColor: 'divider', mt: 1, pt: 2 }}
                  {...a11yProps('zapier')}
                />
                {/* <Tab label="Zapier" /> */}
                {/* <Typography sx={{ p: 2, justifySelf: 'center' }}>Zapier</Typography> */}
                {/* {[...Array(3).keys()].map((i) => {
                if (zapTemplates[i] === undefined) return null
                return (
                  <Tab
                    label={zapTemplates[i].title}
                    key={zapTemplates[i].slug}
                    value={zapTemplates[i].slug}
                    to={`/integrations/${integrationGroupId}/${zapTemplates[i].slug}`}
                    component={Link}
                    replace
                    {...a11yProps(zapTemplates[i].slug)}
                  />
                )
              })} */}
              </Tabs>
            )}
            <Outlet /> {/* This renders the <TabPanel /> component */}
          </Stack>
        </Paper>
      </Box>
    </Box>
  )
}

export function AllIntegrationsBackButton() {
  return (
    <Button sx={{ margin: 2, marginBottom: 0 }} startIcon={<ChevronLeft />} component={Link} to={'/integrations'}>
      <Typography>All Integrations</Typography>
    </Button>
  )
}

export default IntegrationGroupDetailsPage
